import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { listSessions, getSession } from '../utils/sessionManager';

/**
 * SessionsViewPremium - Redesigned sessions view with premium Apple aesthetics
 * Enhanced card design, smooth interactions, professional look
 */
const SessionsViewPremium = ({
  companyIdSlug,
  activeSessionId,
  onResumeSession,
  onStartNewSession
}) => {
  const [sessions, setSessions] = useState([]);
  const [expandedActions, setExpandedActions] = useState(null);

  useEffect(() => {
    if (companyIdSlug) {
      const allSessions = listSessions(companyIdSlug, 50);
      setSessions(allSessions);
    }
  }, [companyIdSlug]);

  // Format export content
  const formatSessionExport = (session, format = 'markdown') => {
    const { messages = [], frameworkName, title } = session;

    if (format === 'markdown') {
      let md = `# ${title || 'Coaching Session'}\n\n`;
      md += `**Date:** ${new Date(session.createdAt).toLocaleDateString()}\n`;
      if (frameworkName) md += `**Framework:** ${frameworkName}\n`;
      md += `**Messages:** ${messages.length}\n\n---\n\n`;

      messages.forEach((msg) => {
        const time = new Date(msg.timestamp || Date.now()).toLocaleTimeString();
        md += `## ${msg.role === 'user' ? 'You' : 'Coach'} (${time})\n\n${msg.content}\n\n`;
        if (msg.sources?.length > 0) {
          md += `**Sources:**\n${msg.sources.map(s => `- ${typeof s === 'string' ? s : s.title || s.name}\n`).join('')}\n`;
        }
      });
      return md;
    }
    // Plain text format
    let txt = `${title || 'Coaching Session'}\n`;
    txt += `Date: ${new Date(session.createdAt).toLocaleDateString()}\n`;
    if (frameworkName) txt += `Framework: ${frameworkName}\n`;
    txt += `${'='.repeat(50)}\n\n`;

    messages.forEach((msg) => {
      const time = new Date(msg.timestamp || Date.now()).toLocaleTimeString();
      txt += `[${time}] ${msg.role === 'user' ? 'You' : 'Coach'}:\n${msg.content}\n\n`;
    });
    return txt;
  };

  const handleCopySession = async (sessionId, e) => {
    e.stopPropagation();
    const session = getSession(companyIdSlug, sessionId);
    if (!session) return;

    try {
      await navigator.clipboard.writeText(formatSessionExport(session, 'txt'));
      setExpandedActions(null);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleExportSession = (sessionId, e) => {
    e.stopPropagation();
    const session = getSession(companyIdSlug, sessionId);
    if (!session) return;

    const content = formatSessionExport(session, 'markdown');
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${session.title || 'session'}-${new Date(session.createdAt).toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExpandedActions(null);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Empty state
  if (sessions.length === 0) {
    return (
      <div className="mm-sessions-premium">
        <div className="mm-view-header">
          <h2 className="mm-view-title">Your Sessions</h2>
          <p className="mm-view-count">0 coaching conversations</p>
        </div>

        <div className="mm-empty-state">
          <div className="mm-empty-icon">💬</div>
          <h3 className="mm-empty-title">No sessions yet</h3>
          <p className="mm-empty-description">
            Start your first coaching conversation from the home screen
          </p>
          <motion.button
            className="mm-cta-voice"
            onClick={onStartNewSession}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="mm-cta-content">
              <span className="mm-cta-label">Start New Session</span>
            </div>
            <div className="mm-cta-icon">+</div>
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="mm-sessions-premium">
      {/* Header */}
      <div className="mm-view-header">
        <h2 className="mm-view-title">Your Sessions</h2>
        <p className="mm-view-count">
          {sessions.length} coaching conversation{sessions.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Sessions List */}
      <div className="mm-sessions-list">
        {sessions.map((session, index) => {
          const isActive = activeSessionId === session.id;
          const showActions = expandedActions === session.id;

          return (
            <motion.div
              key={session.id}
              className="mm-session-card"
            >
              <div
                className="mm-session-card-inner"
                onClick={() => onResumeSession(session.id)}
              >
                <div className="mm-session-card-header">
                  <div className="mm-session-title-group">
                    <h4 className="mm-session-card-title">
                      {session.title}
                      {isActive && <span className="mm-session-active-badge">Active</span>}
                    </h4>
                    {session.frameworkName && (
                      <span className="mm-session-badge">{session.frameworkName}</span>
                    )}
                  </div>
                </div>

                {session.messages && session.messages.length > 0 && (
                  <p className="mm-session-card-preview">
                    {session.messages[session.messages.length - 1]?.content?.substring(0, 120)}
                    {session.messages[session.messages.length - 1]?.content?.length > 120 ? '...' : ''}
                  </p>
                )}

                <div className="mm-session-card-footer">
                  <div className="mm-session-timestamp">
                    <span>{formatDate(session.updatedAt || session.createdAt)}</span>
                    <span>•</span>
                    <span>{formatTime(session.updatedAt || session.createdAt)}</span>
                  </div>
                  <div className="mm-session-stats">
                    <span>{session.messageCount || 0} messages</span>
                  </div>
                </div>
              </div>

              {/* Actions Menu */}
              <div className="mm-session-actions">
                <button
                  className="mm-session-actions-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedActions(showActions ? null : session.id);
                  }}
                  aria-label="Session actions"
                >
                  ⋯
                </button>

                <AnimatePresence>
                  {showActions && (
                    <motion.div
                      className="mm-session-actions-menu"
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="mm-action-button"
                        onClick={(e) => handleCopySession(session.id, e)}
                      >
                        <span className="mm-action-icon">📋</span>
                        <span>Copy</span>
                      </button>
                      <button
                        className="mm-action-button"
                        onClick={(e) => handleExportSession(session.id, e)}
                      >
                        <span className="mm-action-icon">💾</span>
                        <span>Export</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default SessionsViewPremium;
